import { useQuery } from "@tanstack/react-query";
import { WebflowBlogPost, WebflowBlogResponse } from "@/shared/types/blog";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export function useBlogPosts(
  limit: number = 6,
  offset: number = 0,
  skipCategories: boolean = false,
) {
  return useQuery<WebflowBlogResponse>({
    queryKey: ["blog-posts", limit, offset, skipCategories],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (skipCategories) params.set("skipCategories", "true");
      const response = await fetch(`${SUPABASE_URL}/functions/v1/media/blog/posts?${params}`, {
        headers: {
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          apikey: SUPABASE_ANON_KEY,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch blog posts");
      }

      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useAllBlogPosts() {
  return useQuery<WebflowBlogResponse>({
    queryKey: ["blog-posts-all"],
    queryFn: async () => {
      const allItems: WebflowBlogPost[] = [];
      let offset = 0;
      const limit = 100;
      let total = 0;

      do {
        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/media/blog/posts?limit=${limit}&offset=${offset}`,
          {
            headers: {
              Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
              apikey: SUPABASE_ANON_KEY,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch blog posts");
        }

        const data: WebflowBlogResponse = await response.json();
        allItems.push(...data.items);
        total = data.pagination.total;
        offset += limit;
      } while (offset < total);

      return {
        items: allItems,
        pagination: { limit: total, offset: 0, total },
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useBlogPost(slug: string | undefined) {
  return useQuery<WebflowBlogPost | null>({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      if (!slug) return null;

      const response = await fetch(`${SUPABASE_URL}/functions/v1/media/blog/post?slug=${slug}`, {
        headers: {
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          apikey: SUPABASE_ANON_KEY,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch blog post");
      }

      return response.json();
    },
    enabled: !!slug,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
